import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { RootState } from '../store/store';
import { UserRole, Customer, PaymentMode } from '../types';
import { customersService } from '../services/customersService';
import {
  customerPaymentsService,
  CustomerPaymentSummary,
  CustomerPayment,
  TransferPaymentDetails,
} from '../services/customerPaymentsService';

export const AdminCustomerPaymentsPage: React.FC = () => {
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.auth.user);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [summary, setSummary] = useState<CustomerPaymentSummary[]>([]);
  const [payments, setPayments] = useState<CustomerPayment[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [startDate, setStartDate] = useState<string>(format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState<string>(format(endOfMonth(new Date()), 'yyyy-MM-dd'));

  // Modals
  const [isAddPaymentModalOpen, setIsAddPaymentModalOpen] = useState(false);
  const [isPaymentHistoryModalOpen, setIsPaymentHistoryModalOpen] = useState(false);
  const [transferDetails, setTransferDetails] = useState<TransferPaymentDetails | null>(null);

  // Payment form
  const [paymentAmount, setPaymentAmount] = useState<string>('');
  const [paymentDate, setPaymentDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [paymentMethod, setPaymentMethod] = useState<string>('cash');
  const [paymentNotes, setPaymentNotes] = useState<string>('');

  // Transfer selection for payment
  const [customerTransfers, setCustomerTransfers] = useState<any[]>([]);
  const [selectedTransferForPayment, setSelectedTransferForPayment] = useState<string>('');
  const [activeCustomerId, setActiveCustomerId] = useState<string>('');
  const [selectedCustomerPaymentMode, setSelectedCustomerPaymentMode] = useState<PaymentMode>(PaymentMode.PER_TRANSFER);

  // Check access
  const isCustomerManager = false;
  const isOrgLevel = user?.role === UserRole.MANAGER ||
                     user?.role === UserRole.ORG_ADMIN ||
                     user?.role === UserRole.PLATFORM_ADMIN;
  const canViewPayments = isOrgLevel;
  const canManagePayments = isOrgLevel;

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    if (customers.length > 0) {
      loadData();
    }
  }, [selectedCustomerId, startDate, endDate, customers]);

  const loadCustomers = async () => {
    try {
      const data = await customersService.getAll(true);
      setCustomers(data);
    } catch (error) {
      toast.error(t('payments.failedToLoadCustomers'));
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const filters = {
        customerId: selectedCustomerId || undefined,
        startDate,
        endDate,
      };

      const [summaryData, paymentsData] = await Promise.all([
        customerPaymentsService.getSummary(filters),
        customerPaymentsService.getAll(filters),
      ]);

      setSummary(summaryData);
      setPayments(paymentsData);
    } catch (error: any) {
      toast.error(t('payments.failedToLoadData'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openAddPaymentModal = async (customerId: string) => {
    try {
      // Find the customer to check payment mode
      const customer = customers.find(c => c.id === customerId);
      const paymentMode = customer?.paymentMode || PaymentMode.PER_TRANSFER;
      setSelectedCustomerPaymentMode(paymentMode);
      setActiveCustomerId(customerId);

      // Only load transfers for PER_TRANSFER mode
      if (paymentMode === PaymentMode.PER_TRANSFER) {
        const unpaidTransfers = await customerPaymentsService.getUnpaidTransfers(customerId);
        setCustomerTransfers(unpaidTransfers);
      } else {
        setCustomerTransfers([]);
      }

      setIsAddPaymentModalOpen(true);
    } catch (error) {
      toast.error(t('payments.failedToLoadTransfers'));
    }
  };

  const handleAddPayment = async (e: React.FormEvent) => {
    e.preventDefault();

    // Only require transfer for PER_TRANSFER mode
    if (selectedCustomerPaymentMode === PaymentMode.PER_TRANSFER && !selectedTransferForPayment) {
      toast.error(t('payments.selectTransferRequired'));
      return;
    }

    if (!paymentAmount || Number(paymentAmount) <= 0) {
      toast.error(t('payments.validAmountRequired'));
      return;
    }

    try {
      await customerPaymentsService.create({
        transferId: selectedCustomerPaymentMode === PaymentMode.PER_TRANSFER ? selectedTransferForPayment : undefined,
        customerId: activeCustomerId,
        amount: Number(paymentAmount),
        paymentDate,
        paymentMethod,
        notes: paymentNotes || undefined,
      });

      toast.success(t('payments.paymentRecorded'));
      setIsAddPaymentModalOpen(false);
      resetPaymentForm();
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('payments.failedToRecord'));
    }
  };

  const resetPaymentForm = () => {
    setPaymentAmount('');
    setPaymentDate(format(new Date(), 'yyyy-MM-dd'));
    setPaymentMethod('cash');
    setPaymentNotes('');
    setSelectedTransferForPayment('');
    setCustomerTransfers([]);
    setActiveCustomerId('');
  };

  const openPaymentHistory = async (transferId: string) => {
    try {
      const details = await customerPaymentsService.getTransferDetails(transferId);
      setTransferDetails(details);
      setIsPaymentHistoryModalOpen(true);
    } catch (error) {
      toast.error(t('payments.failedToLoadHistory'));
    }
  };

  const handleDeletePayment = async (paymentId: string) => {
    if (!window.confirm(t('payments.deletePaymentConfirm'))) {
      return;
    }

    try {
      await customerPaymentsService.delete(paymentId);
      toast.success(t('payments.paymentDeleted'));
      loadData();
      if (transferDetails) {
        const updated = await customerPaymentsService.getTransferDetails(transferDetails.transfer.id);
        setTransferDetails(updated);
      }
    } catch (error) {
      toast.error(t('payments.failedToDelete'));
    }
  };

  if (!canViewPayments) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('payments.accessDenied')}</h2>
          <p className="text-gray-600">{t('payments.managersOnly')}</p>
        </div>
      </div>
    );
  }

  // Calculate totals
  const totalAmount = summary.reduce((sum, p) => sum + Number(p.totalAmount), 0);
  const totalPaid = summary.reduce((sum, p) => sum + Number(p.totalPaid), 0);
  const totalPending = summary.reduce((sum, p) => sum + Number(p.totalPending), 0);

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
          {isCustomerManager ? 'Ödeme Durumu' : 'Customer Payments (Tahsilat)'}
        </h1>
        <p className="text-gray-600 dark:text-gray-300 mt-1 text-sm sm:text-base">
          {isCustomerManager ? 'Transfer ödemelerinizin durumunu görüntüleyin' : 'Track and manage customer payments'}
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Filtreler</h2>
        <div className={`grid grid-cols-1 ${isCustomerManager ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-4`}>
          {!isCustomerManager && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Customer
              </label>
              <select
                value={selectedCustomerId}
                onChange={(e) => setSelectedCustomerId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white"
              >
                <option value="">All Customers</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 mb-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg shadow-lg p-4 sm:p-6 border-2 border-blue-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-10 w-10 sm:h-12 sm:w-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-blue-600">{isCustomerManager ? 'Toplam Tutar' : 'Toplam Hakediş'}</p>
              <p className="text-xl sm:text-3xl font-bold text-blue-900">{totalAmount.toFixed(2)} <span className="text-sm sm:text-base">TL</span></p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg shadow-lg p-4 sm:p-6 border-2 border-green-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-10 w-10 sm:h-12 sm:w-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-green-600">{isCustomerManager ? 'Ödenen' : 'Tahsil Edilen'}</p>
              <p className="text-xl sm:text-3xl font-bold text-green-900">{totalPaid.toFixed(2)} <span className="text-sm sm:text-base">TL</span></p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg shadow-lg p-4 sm:p-6 border-2 border-orange-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-10 w-10 sm:h-12 sm:w-12 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-orange-600">{isCustomerManager ? 'Kalan Borç' : 'Bekleyen Alacak'}</p>
              <p className="text-xl sm:text-3xl font-bold text-orange-900">{totalPending.toFixed(2)} <span className="text-sm sm:text-base">TL</span></p>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Summary Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">{isCustomerManager ? 'Ödeme Özeti' : 'Müşteri Özeti'}</h2>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-600">Loading...</div>
        ) : summary.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No data found for selected filters</div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-gray-200">
              {summary.map((item) => (
                <div key={item.customerId} className="p-4">
                  {/* Customer Name & Status */}
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-medium text-gray-900">{item.customerName}</p>
                      <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-semibold rounded-full ${
                        item.paymentMode === PaymentMode.LUMP_SUM
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {item.paymentMode === PaymentMode.LUMP_SUM ? 'Cari' : 'Transfer'}
                      </span>
                    </div>
                    {Number(item.totalPending) > 0 ? (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                        {isCustomerManager ? 'Borç Var' : 'Alacak Var'}
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        Tamamlandı
                      </span>
                    )}
                  </div>

                  {/* Transfer Count */}
                  <div className="text-sm text-gray-600 mb-3">
                    {item.transferCount} transfer • {item.paidTransferCount} ödendi • {item.pendingTransferCount} bekliyor
                  </div>

                  {/* Amounts */}
                  <div className="grid grid-cols-3 gap-2 text-center mb-3">
                    <div className="bg-gray-50 rounded p-2">
                      <p className="text-xs text-gray-500">{isCustomerManager ? 'Tutar' : 'Toplam'}</p>
                      <p className="font-bold text-gray-900 text-sm">{Number(item.totalAmount).toFixed(0)} TL</p>
                    </div>
                    <div className="bg-green-50 rounded p-2">
                      <p className="text-xs text-green-600">{isCustomerManager ? 'Ödenen' : 'Tahsil'}</p>
                      <p className="font-bold text-green-700 text-sm">{Number(item.totalPaid).toFixed(0)} TL</p>
                    </div>
                    <div className="bg-orange-50 rounded p-2">
                      <p className="text-xs text-orange-600">{isCustomerManager ? 'Kalan' : 'Bekleyen'}</p>
                      <p className="font-bold text-orange-700 text-sm">{Number(item.totalPending).toFixed(0)} TL</p>
                    </div>
                  </div>

                  {/* Action Button */}
                  {canManagePayments && (
                    <button
                      onClick={() => openAddPaymentModal(item.customerId)}
                      className="w-full py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 active:bg-blue-800"
                    >
                      Tahsilat Ekle
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Müşteri</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mod</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transferler</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Toplam Tutar</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tahsil Edilen</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bekleyen</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
                    {canManagePayments && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">İşlemler</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {summary.map((item) => (
                    <tr key={item.customerId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {item.customerName}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          item.paymentMode === PaymentMode.LUMP_SUM
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {item.paymentMode === PaymentMode.LUMP_SUM ? 'Cari' : 'Transfer'}
                        </span>
                        {item.paymentMode === PaymentMode.LUMP_SUM && item.balance !== undefined && (
                          <div className="text-xs mt-1 text-blue-600">
                            Bakiye: {Number(item.balance).toFixed(2)} TL
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        <div>{item.transferCount} toplam</div>
                        <div className="text-xs text-gray-500">
                          {item.paidTransferCount} ödendi • {item.pendingTransferCount} bekliyor
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-gray-900">
                        {Number(item.totalAmount).toFixed(2)} TL
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-green-600">
                        {Number(item.totalPaid).toFixed(2)} TL
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-orange-600">
                        {Number(item.totalPending).toFixed(2)} TL
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {Number(item.totalPending) > 0 ? (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                            {isCustomerManager ? 'Borç Var' : 'Alacak Var'}
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            Tamamlandı
                          </span>
                        )}
                      </td>
                      {canManagePayments && (
                        <td className="px-6 py-4 text-sm">
                          <button
                            onClick={() => openAddPaymentModal(item.customerId)}
                            className="text-blue-600 hover:text-blue-900 font-medium"
                          >
                            Tahsilat Ekle
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Payment History Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">{isCustomerManager ? 'Son Ödemeler' : 'Son Tahsilatlar'}</h2>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-600">Loading...</div>
        ) : payments.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Henüz tahsilat kaydı yok</div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-gray-200">
              {payments.map((payment) => (
                <div key={payment.id} className="p-4">
                  {/* Header: Customer & Amount */}
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-medium text-gray-900">{payment.customer?.name || 'N/A'}</p>
                      <p className="text-xs text-gray-500">{format(new Date(payment.paymentDate), 'dd.MM.yyyy')}</p>
                    </div>
                    <p className="text-lg font-bold text-green-600">{Number(payment.amount).toFixed(2)} TL</p>
                  </div>

                  {/* Transfer Info */}
                  <div className="text-sm mb-2">
                    {payment.transfer ? (
                      <p className="text-gray-600">
                        #{payment.transfer.transferId} • {payment.transfer.pickupLocation} → {payment.transfer.dropoffLocation}
                      </p>
                    ) : (
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                        Toplu Tahsilat
                      </span>
                    )}
                  </div>

                  {/* Footer: Method & Actions */}
                  <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                    <span className="text-xs text-gray-500 capitalize">{payment.paymentMethod.replace('_', ' ')}</span>
                    <div className="flex gap-3">
                      {payment.transferId && (
                        <button
                          onClick={() => openPaymentHistory(payment.transferId!)}
                          className="text-blue-600 text-sm font-medium"
                        >
                          Görüntüle
                        </button>
                      )}
                      {canManagePayments && (
                        <button
                          onClick={() => handleDeletePayment(payment.id)}
                          className="text-red-600 text-sm font-medium"
                        >
                          Sil
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tarih</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Müşteri</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transfer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tutar</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ödeme Şekli</th>
                    {!isCustomerManager && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kaydeden</th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {payments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {format(new Date(payment.paymentDate), 'dd.MM.yyyy')}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {payment.customer?.name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {payment.transfer ? (
                          <>
                            <div>#{payment.transfer.transferId}</div>
                            <div className="text-xs text-gray-500">
                              {payment.transfer.pickupLocation} → {payment.transfer.dropoffLocation}
                            </div>
                          </>
                        ) : (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            Toplu Tahsilat
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-green-600">
                        {Number(payment.amount).toFixed(2)} TL
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 capitalize">
                        {payment.paymentMethod.replace('_', ' ')}
                      </td>
                      {!isCustomerManager && (
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {payment.recordedBy?.fullName || 'N/A'}
                        </td>
                      )}
                      <td className="px-6 py-4 text-sm space-x-2">
                        {payment.transferId && (
                          <button
                            onClick={() => openPaymentHistory(payment.transferId!)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Görüntüle
                          </button>
                        )}
                        {canManagePayments && (
                          <button
                            onClick={() => handleDeletePayment(payment.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Sil
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Add Payment Modal */}
      {isAddPaymentModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto m-4">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">
                {selectedCustomerPaymentMode === PaymentMode.LUMP_SUM ? 'Toplu Tahsilat Gir' : 'Tahsilat Ekle'}
              </h2>
              <p className="text-gray-600 mt-1">
                {selectedCustomerPaymentMode === PaymentMode.LUMP_SUM
                  ? 'Bu tahsilat müşterinin bakiyesinden düşülecektir'
                  : 'Belirli bir transfer için tahsilat kaydı oluşturun'}
              </p>
            </div>

            <form onSubmit={handleAddPayment} className="p-6 space-y-4">
              {/* Payment Mode Info */}
              <div className={`p-3 rounded-lg ${selectedCustomerPaymentMode === PaymentMode.LUMP_SUM ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'}`}>
                <p className="text-sm font-medium">
                  Ödeme Modu: {selectedCustomerPaymentMode === PaymentMode.LUMP_SUM ? (
                    <span className="text-blue-600">Toplu Ödeme (Cari Hesap)</span>
                  ) : (
                    <span className="text-gray-600">Transfer Bazlı</span>
                  )}
                </p>
                {selectedCustomerPaymentMode === PaymentMode.LUMP_SUM && (
                  <p className="text-xs text-blue-600 mt-1">
                    Bu müşteri cari hesap sistemi kullanıyor. Tahsilat bakiyeden düşülecektir.
                  </p>
                )}
              </div>

              {/* Transfer Selection - Only for PER_TRANSFER mode */}
              {selectedCustomerPaymentMode === PaymentMode.PER_TRANSFER && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Transfer Seçin <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedTransferForPayment}
                    onChange={(e) => {
                      setSelectedTransferForPayment(e.target.value);
                      const transfer = customerTransfers.find((t: any) => t.id === e.target.value);
                      if (transfer) {
                        const remaining = transfer.remaining || transfer.customerPrice || 0;
                        setPaymentAmount(remaining.toString());
                      }
                    }}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Transfer seçin...</option>
                    {customerTransfers.map((transfer: any) => (
                      <option key={transfer.id} value={transfer.id}>
                        #{transfer.transferId} - {format(new Date(transfer.transferDate), 'dd.MM.yyyy')} - {' '}
                        {transfer.pickupLocation} → {transfer.dropoffLocation} - {' '}
                        Kalan: {transfer.remaining?.toFixed(2) || transfer.customerPrice} TL
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tahsilat Tutarı (TL) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tahsilat Tarihi <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ödeme Şekli
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="cash">Nakit</option>
                  <option value="bank_transfer">Banka Transferi</option>
                  <option value="check">Çek</option>
                  <option value="credit_card">Kredi Kartı</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notlar (İsteğe Bağlı)
                </label>
                <textarea
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder={t('payments.additionalNotes')}
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setIsAddPaymentModalOpen(false);
                    resetPaymentForm();
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Tahsilatı Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment History Modal */}
      {isPaymentHistoryModalOpen && transferDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-screen overflow-y-auto m-4">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Tahsilat Geçmişi</h2>
              <p className="text-gray-600 mt-1">
                Transfer #{transferDetails.transfer.transferCode || '-'} - {transferDetails.transfer.customerName}
              </p>
            </div>

            <div className="p-6">
              {/* Transfer Summary */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Tarih</p>
                    <p className="font-semibold">{format(new Date(transferDetails.transfer.transferDate), 'dd.MM.yyyy')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Güzergah</p>
                    <p className="font-semibold">
                      {transferDetails.transfer.pickupLocation} → {transferDetails.transfer.dropoffLocation}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Toplam Tutar</p>
                    <p className="font-semibold text-blue-600">{transferDetails.totalPrice.toFixed(2)} TL</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Tahsil Edilen</p>
                    <p className="font-semibold text-green-600">{transferDetails.totalPaid.toFixed(2)} TL</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Kalan</p>
                    <p className="font-semibold text-orange-600">{transferDetails.remaining.toFixed(2)} TL</p>
                  </div>
                </div>
              </div>

              {/* Payments List */}
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Tahsilat Kayıtları</h3>
              {transferDetails.payments.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Henüz tahsilat kaydı yok</p>
              ) : (
                <div className="space-y-3">
                  {transferDetails.payments.map((payment) => (
                    <div key={payment.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="font-bold text-green-600">{Number(payment.amount).toFixed(2)} TL</span>
                            <span className="text-xs bg-gray-100 px-2 py-1 rounded">{payment.paymentMethod}</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {format(new Date(payment.paymentDate), 'dd MMMM yyyy')}
                          </p>
                          {payment.notes && (
                            <p className="text-sm text-gray-500 mt-1 italic">{payment.notes}</p>
                          )}
                          {!isCustomerManager && (
                            <p className="text-xs text-gray-400 mt-1">
                              Kaydeden: {payment.recordedBy?.fullName} - {' '}
                              {format(new Date(payment.createdAt), 'dd.MM.yyyy HH:mm')}
                            </p>
                          )}
                        </div>
                        {canManagePayments && (
                          <button
                            onClick={() => handleDeletePayment(payment.id)}
                            className="text-red-600 hover:text-red-900 text-sm"
                          >
                            Sil
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setIsPaymentHistoryModalOpen(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
